import { plural } from "@lingui/core/macro";
import { Trans, useLingui } from "@lingui/react/macro";
import { observer } from "mobx-react-lite";
import { useEffect, useState } from "react";

import { ClinicalStudyVisitDefinition } from "../../../api";
import { ApiConnector } from "../../../api/api-connector";
import { FormRow } from "../../../components/ui-kit/form/form-row";
import { FormTable } from "../../../components/ui-kit/form/form-table";
import { DataTable } from "../../../components/ui-kit/tables/data-table";
import { Tags } from "../../../components/ui-kit/tags";
import { RequiredDataType } from "../../../constants/enum-to-text";
import { DataModel } from "../../../models/data-model";
import { prettyVisitType } from "../utils";

type VisitDataRow = ClinicalStudyVisitDefinition["data_list"][number] & {
  key: number;
};

export const StudyVisitTable = observer(function StudyVisitTable(props: {
  visit: ClinicalStudyVisitDefinition;
  api: ApiConnector;
}) {
  const { t } = useLingui();
  const [visitsModel] = useState(
    () =>
      new DataModel<VisitDataRow>(
        [],
        props.api.permissions,
        props.api.requireUserUUID(),
      ),
  );
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    // Data list rows have no id field — synthesize an index-based key.
    visitsModel.updateData(
      props.visit.data_list.map((item, index) => ({ ...item, key: index })),
    );
  }, [props.visit.data_list, visitsModel]);

  return (
    <div>
      <div className="flex gap-2 items-center">
        <h3 className="mb-5 mt-5 text-base font-bold">
          {prettyVisitType(props.visit.visit_type)}
        </h3>

        <button
          className="border-none text-xs"
          onClick={() => setExpanded(!expanded)}
        >
          {expanded ? "▲" : "▼"}
        </button>
      </div>

      {expanded && (
        <>
          <FormTable>
            <FormRow label={t`Schedule`} data={props.visit.visit_schedule} />
            <FormRow
              label={t`Location`}
              data={props.visit.visit_location_type}
            />
            <FormRow label={t`Comment`} data={props.visit.comment} />
            <FormRow
              label={t`Tags`}
              data={<Tags tags={props.visit.tags ?? []} />}
            />
          </FormTable>
          <h4 className="mt-5 mb-5 font-bold">
            <Trans>Data list:</Trans>
          </h4>
          <DataTable
            model={visitsModel}
            itemKeyField="key"
            filterNoun={(count) =>
              t`${plural(count, { one: "item", other: "items" })}`
            }
            columns={[
              {
                header: <Trans>Name/Procedure</Trans>,
                // data_list is a discriminated union — each variant carries
                // exactly one of these naming fields.
                cell: ({ item }) => {
                  const row = item as Partial<
                    Record<
                      "data_name" | "procedure" | "measurement_condition",
                      string
                    >
                  >;
                  return (
                    row.data_name || row.procedure || row.measurement_condition
                  );
                },
              },
              {
                header: <Trans>Type</Trans>,
                attr: "data_type",
                type: "enum",
                values: RequiredDataType,
                filter: {
                  kind: "multi",
                  group: t`Data`,
                  options: new Map(Object.entries(RequiredDataType)),
                },
              },
              {
                header: <Trans>Required</Trans>,
                attr: "required",
                type: "boolean",
                filter: {
                  kind: "bool",
                  group: t`Data`,
                  predicate: (item) => !!item.required,
                  toggleLabel: t`Required only`,
                },
              },
            ]}
            rowsPerPage={Number.MAX_VALUE}
          />
        </>
      )}
    </div>
  );
});
